"use client";

import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import ReactHtmlParser from "@/src/utils/html-parser";
import Kicker from "@/src/components/common/broadsheet/kicker";
import {
  actionSx,
  dottedRule,
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  HEADING_SIZE,
} from "@/src/components/common/broadsheet/tokens";
import {
  ConsentValue,
  OPEN_COOKIE_SETTINGS_EVENT,
  readStoredConsent,
  storeConsent,
} from "@/src/components/common/cookie-manager/consent-storage";

declare const gtag: (...args: any[]) => void;

const PURPOSES = [
  {
    key: "analytics" as const,
    configKey: "consent_setting_analytics",
    fallbackLabel: "Analytics",
    description:
      "How the site is used — which pages are read and how readers arrive.",
  },
  {
    key: "personalization" as const,
    configKey: "consent_setting_personalization",
    fallbackLabel: "Personalization",
    description: "Tailoring what you are shown to your interests.",
  },
  {
    key: "marketing" as const,
    configKey: "consent_setting_marketing",
    fallbackLabel: "Marketing",
    description: "Measuring and targeting advertising off this site.",
  },
];

export default function CookieModal({ journal }: { journal: any }) {
  const [open, setOpen] = useState(false);
  // Pre-ticked boxes are not valid consent (CJEU C-673/17 "Planet49").
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [personalizationConsent, setPersonalizationConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const hasStoredChoice = useRef(false);

  const consentConfig = journal.options?.template_configs?.google_consent;

  const syncFromStored = useCallback(() => {
    const stored = readStoredConsent();
    hasStoredChoice.current = stored !== null;
    if (stored) {
      setMarketingConsent(stored.adStorage === "granted");
      setAnalyticsConsent(stored.analyticsStorage === "granted");
      setPersonalizationConsent(stored.adPersonalization === "granted");
    }
    return stored;
  }, []);

  useEffect(() => {
    if (!syncFromStored()) {
      setOpen(true);
    }
    const reopen = () => {
      syncFromStored();
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, [syncFromStored]);

  const updateConsent = (
    marketing: ConsentValue,
    analytics: ConsentValue,
    personalization: ConsentValue,
  ) => {
    if (typeof gtag !== "undefined") {
      gtag("consent", "update", {
        ad_storage: marketing,
        ad_user_data: marketing,
        analytics_storage: analytics,
        ad_personalization: personalization,
      });
    }
    storeConsent({
      adStorage: marketing,
      adUserData: marketing,
      analyticsStorage: analytics,
      adPersonalization: personalization,
    });
    hasStoredChoice.current = true;
  };

  const handleClose = (
    consentType: "acceptAll" | "acceptSelected" | "rejectAll",
  ) => {
    setOpen(false);

    switch (consentType) {
      case "acceptAll": {
        setMarketingConsent(true);
        setAnalyticsConsent(true);
        setPersonalizationConsent(true);
        updateConsent("granted", "granted", "granted");
        break;
      }

      case "acceptSelected": {
        updateConsent(
          marketingConsent ? "granted" : "denied",
          analyticsConsent ? "granted" : "denied",
          personalizationConsent ? "granted" : "denied",
        );
        break;
      }

      case "rejectAll": {
        setMarketingConsent(false);
        setAnalyticsConsent(false);
        setPersonalizationConsent(false);
        updateConsent("denied", "denied", "denied");
        break;
      }

      default:
        break;
    }
  };

  const handleDismiss = () => {
    if (hasStoredChoice.current) {
      syncFromStored();
      setOpen(false);
    } else {
      handleClose("rejectAll");
    }
  };

  const state = {
    analytics: [analyticsConsent, setAnalyticsConsent] as const,
    personalization: [
      personalizationConsent,
      setPersonalizationConsent,
    ] as const,
    marketing: [marketingConsent, setMarketingConsent] as const,
  };

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      aria-labelledby="cookie-modal-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 0,
            border: "1px solid",
            borderColor: "custom.ruleHairline",
            backgroundColor: "custom.newsprint",
            backgroundImage: "none",
            boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
            m: { xs: 1, sm: 2 },
          },
        },
      }}
    >
      <Box
        sx={{ px: { xs: 1.25, sm: 1.75 }, pt: 1.25, pb: 0.75, ...doubleRule }}
      >
        <Kicker sx={{ mb: 0.25 }}>Privacy</Kicker>
        <Typography
          component="h2"
          id="cookie-modal-title"
          sx={{
            m: 0,
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: HEADING_SIZE.page,
            lineHeight: 1.1,
            letterSpacing: "0.01em",
            color: "custom.ink",
          }}
        >
          {consentConfig?.consent_setting?.label ?? "Cookie settings"}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 1.75 }, py: 1 }}>
        <Box
          sx={{
            fontFamily: FONT_TEXT,
            fontSize: "0.9375rem",
            lineHeight: 1.6,
            color: "custom.inkMuted",
            "& p": { m: 0, mb: 0.5 },
            "& p:last-of-type": { mb: 0 },
            "& a": { color: "custom.link" },
          }}
        >
          {consentConfig?.consent_setting?.caption ? (
            ReactHtmlParser(consentConfig.consent_setting.caption)
          ) : (
            <p>
              Strictly necessary cookies keep the site working and are always
              on. Everything below is optional and off until you turn it on.
            </p>
          )}
        </Box>

        <Box sx={{ mt: 1 }}>
          {PURPOSES.map((purpose) => {
            const [checked, setChecked] = state[purpose.key];
            const label =
              consentConfig?.[purpose.configKey]?.label ??
              purpose.fallbackLabel;

            return (
              <Box
                key={purpose.key}
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 0.75,
                  cursor: "pointer",
                  ...dottedRule,
                  "&:first-of-type": {
                    borderTop: "1px dotted",
                    borderTopColor: "custom.ruleHairline",
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      fontFamily: FONT_UI,
                      fontSize: "0.8125rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "custom.ink",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      fontFamily: FONT_TEXT,
                      fontSize: "0.8125rem",
                      lineHeight: 1.5,
                      color: "custom.inkMuted",
                      mt: 0.0625,
                    }}
                  >
                    {purpose.description}
                  </Typography>
                </Box>
                <Box
                  component="input"
                  type="checkbox"
                  checked={checked}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setChecked(event.target.checked)
                  }
                  aria-label={label}
                  sx={{
                    appearance: "none",
                    flex: "none",
                    m: 0,
                    mt: 0.125,
                    width: "1.5rem",
                    height: "1.5rem",
                    display: "grid",
                    placeContent: "center",
                    cursor: "pointer",
                    border: "1.5px solid",
                    borderColor: "custom.link",
                    backgroundColor: "custom.newsprintRaised",
                    transition: "background-color .15s ease",
                    "&::after": {
                      content: '""',
                      width: "0.5rem",
                      height: "0.3rem",
                      borderLeft: "2px solid",
                      borderBottom: "2px solid",
                      borderColor: "custom.inverted",
                      transform: "rotate(-45deg) translate(1px, -2px)",
                      opacity: 0,
                    },
                    "&:checked": { backgroundColor: "custom.link" },
                    "&:checked::after": { opacity: 1 },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "custom.link",
                      outlineOffset: "2px",
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 1.25, sm: 1.75 },
          pt: 0.75,
          pb: 1.25,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          borderTop: "1px solid",
          borderColor: "custom.ruleHairline",
        }}
      >
        <Button
          disableElevation
          onClick={() => handleClose("acceptAll")}
          sx={{
            ...actionSx("solid"),
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
          }}
        >
          Accept all
        </Button>
        <Button
          disableElevation
          onClick={() => handleClose("rejectAll")}
          sx={{ ...actionSx("ink"), flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
        >
          Reject all
        </Button>
        <Button
          disableElevation
          onClick={() => handleClose("acceptSelected")}
          sx={{
            ...actionSx("outline"),
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            ml: { sm: "auto" },
          }}
        >
          Save selection
        </Button>
      </Box>
    </Dialog>
  );
}
